import {
  EllipsisVerticalIcon,
  Eye,
  MinusIcon,
  PlusIcon,
  RefreshCw,
  TrashIcon,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PermissionGuard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";
import { formatPrice } from "@/utils/monetary-formatter";

import { isInfinityAssistanceProduct } from "../products.assistance-variant";
import { EProductType, TProduct } from "../products.type";
import { isInfinitySyncedProduct } from "../products-sync-origin";
import {
  collectProductPriceEntries,
  type TProductPreviewContext,
  type TProductPriceEntry,
} from "../utils/soulhub-gateway-pricing.util";

type TProductPreviewRow = {
  id: string;
  /** Linhas de capital/faixa (omitidas quando falsas para Garantia / Roubo só com JSON). */
  showCapital: boolean;
  capitalValue: number;
  isAssistance: boolean;
  minimumPrice?: number;
  premiumValue: number;
  netPremium: number | null | undefined;
  commissionedPrice: number | null | undefined;
  premiumPriceType?: "net" | "commissioned";
  /** Só nome/descrição (plano adicional sem preço na raiz). */
  planOnly?: boolean;
  planName: string;
  description: string | null;
  priceEntries: TProductPriceEntry[];
};

function capitalRangeHasMonetaryValues(capital: TProduct["capital_ranges"][number]): boolean {
  return (
    (capital.premium_value ?? 0) > 0 ||
    (capital.net_premium ?? 0) > 0 ||
    (capital.net_price ?? 0) > 0 ||
    (capital.minimum_price ?? 0) > 0
  );
}

function getSoulhubAssistanceConfigPrices(p: TProduct) {
  const cfgObj =
    p.assistance_product_config && typeof p.assistance_product_config === "object"
      ? (p.assistance_product_config as Record<string, unknown>)
      : null;

  const netPrice =
    cfgObj && typeof cfgObj.net_price === "number" ? cfgObj.net_price : null;
  const commissioned =
    cfgObj && typeof cfgObj.commissioned_price === "number"
      ? cfgObj.commissioned_price
      : null;

  let premiumType = cfgObj?.premium_price_type as string | undefined;
  if (premiumType !== "net" && premiumType !== "commissioned") {
    if (commissioned != null) {
      premiumType = "commissioned";
    } else if (netPrice != null) {
      premiumType = "net";
    }
  }

  return { cfgObj, netPrice, commissioned, premiumType };
}

function withPriceEntries(
  p: TProduct,
  row: Omit<TProductPreviewRow, "priceEntries">,
  context: TProductPreviewContext,
): TProductPreviewRow {
  return {
    ...row,
    priceEntries: collectProductPriceEntries(p, context),
  };
}

function getProductPreviewRows(p: TProduct): TProductPreviewRow[] {
  const isSoulhubAssistance =
    p.type === EProductType.INSURANCE_ASSISTANCE &&
    !isInfinityAssistanceProduct(p);

  if (isSoulhubAssistance) {
    const { cfgObj, netPrice, commissioned, premiumType } =
      getSoulhubAssistanceConfigPrices(p);
    const ranges = p.capital_ranges || [];
    const hasConfigPremium =
      premiumType === "net" || premiumType === "commissioned";

    if (ranges.length > 0) {
      return ranges.map((capital) => {
        const rangeNet =
          capital.net_premium ??
          (typeof capital.net_price === "number" ? capital.net_price : null);
        const hasRangePrices = capitalRangeHasMonetaryValues(capital);
        const useConfigPrices = !hasRangePrices && hasConfigPremium;

        const planName =
          (capital as { plan_name?: string | null }).plan_name || p.name || "—";

        const previewContext: TProductPreviewContext = {
          cfgObj,
          premiumPriceType: premiumType as "net" | "commissioned" | undefined,
          capitalRange: capital,
        };

        if (useConfigPrices) {
          return withPriceEntries(
            p,
            {
              id: capital.id,
              showCapital: true,
              capitalValue: capital.capital_value ?? 0,
              isAssistance: true,
              minimumPrice: capital.minimum_price ?? undefined,
              premiumValue:
                premiumType === "commissioned"
                  ? commissioned ?? 0
                  : netPrice ?? 0,
              netPremium: netPrice,
              commissionedPrice: commissioned,
              premiumPriceType: undefined,
              planName,
              description: capital.description,
            },
            previewContext,
          );
        }

        if (!hasRangePrices && !hasConfigPremium) {
          return withPriceEntries(
            p,
            {
              id: capital.id,
              showCapital: false,
              capitalValue: 0,
              isAssistance: true,
              premiumValue: 0,
              netPremium: undefined,
              commissionedPrice: undefined,
              premiumPriceType: undefined,
              planOnly: true,
              planName,
              description: capital.description,
            },
            { ...previewContext, planOnly: true },
          );
        }

        return withPriceEntries(
          p,
          {
            id: capital.id,
            showCapital: true,
            capitalValue: capital.capital_value,
            isAssistance: true,
            minimumPrice: capital.minimum_price ?? undefined,
            premiumValue: capital.premium_value,
            netPremium: rangeNet,
            commissionedPrice: null,
            premiumPriceType: undefined,
            planName,
            description: capital.description,
          },
          previewContext,
        );
      });
    }

    if (hasConfigPremium) {
      return [
        withPriceEntries(
          p,
          {
            id: `${p.id}-premium-root`,
            showCapital: false,
            capitalValue: 0,
            isAssistance: true,
            premiumValue:
              premiumType === "commissioned" ? commissioned ?? 0 : netPrice ?? 0,
            netPremium: netPrice,
            commissionedPrice: commissioned,
            premiumPriceType: premiumType as "net" | "commissioned",
            planName: p.name || "—",
            description: null,
          },
          {
            cfgObj,
            premiumPriceType: premiumType as "net" | "commissioned",
          },
        ),
      ];
    }
  }

  const isWarrantyTheft =
    p.type === EProductType.WARRANTY_INSURANCE ||
    p.type === EProductType.THEFT_INSURANCE;

  if (isWarrantyTheft) {
    const cfg =
      p.type === EProductType.WARRANTY_INSURANCE
        ? p.warranty_product_config
        : p.theft_product_config;
    const cfgObj =
      cfg && typeof cfg === "object" ? (cfg as Record<string, unknown>) : null;

    const hasLegacyRanges =
      Array.isArray(p.capital_ranges) && p.capital_ranges.length > 0;

    if (!hasLegacyRanges && cfgObj) {
      let premiumType = cfgObj.premium_price_type as string | undefined;
      const netPrice =
        typeof cfgObj.net_price === "number" ? cfgObj.net_price : null;
      const commissioned =
        typeof cfgObj.commissioned_price === "number"
          ? cfgObj.commissioned_price
          : null;

      if (premiumType !== "net" && premiumType !== "commissioned") {
        if (commissioned != null) {
          premiumType = "commissioned";
        } else if (netPrice != null) {
          premiumType = "net";
        }
      }

      const plans = Array.isArray(cfgObj.plans)
        ? (cfgObj.plans as Array<Record<string, unknown>>)
        : [];

      const rows: TProductPreviewRow[] = [];

      if (premiumType === "net" || premiumType === "commissioned") {
        rows.push(
          withPriceEntries(
            p,
            {
              id: `${p.id}-premium-root`,
              showCapital: false,
              capitalValue: 0,
              isAssistance: false,
              premiumValue:
                premiumType === "commissioned"
                  ? commissioned ?? 0
                  : netPrice ?? 0,
              netPremium: netPrice,
              commissionedPrice: commissioned,
              premiumPriceType: premiumType,
              planName: p.name || "—",
              description: null,
            },
            {
              cfgObj,
              premiumPriceType: premiumType,
            },
          ),
        );
      }

      plans.forEach((plan, idx) => {
        rows.push(
          withPriceEntries(
            p,
            {
              id: `${p.id}-plan-${idx}`,
              showCapital: false,
              capitalValue: 0,
              isAssistance: false,
              premiumValue: 0,
              netPremium: undefined,
              commissionedPrice: undefined,
              premiumPriceType: undefined,
              planOnly: true,
              planName: String(plan.name ?? `Plano ${idx + 1}`),
              description:
                plan.description != null ? String(plan.description) : null,
            },
            { planOnly: true },
          ),
        );
      });

      return rows.length > 0 ? rows : [];
    }
  }

  const rangeRows = (p.capital_ranges || []).map((capital) =>
    withPriceEntries(
      p,
      {
        id: capital.id,
        showCapital: true,
        capitalValue: capital.capital_value,
        isAssistance: p.type === EProductType.INSURANCE_ASSISTANCE,
        minimumPrice: capital.minimum_price ?? undefined,
        premiumValue: capital.premium_value,
        netPremium:
          capital.net_premium ??
          (typeof capital.net_price === "number" ? capital.net_price : null),
        commissionedPrice: null,
        premiumPriceType: undefined,
        planName:
          (capital as { plan_name?: string | null }).plan_name || p.name || "—",
        description: capital.description,
      },
      { capitalRange: capital },
    ),
  );

  if (rangeRows.length > 0) {
    return rangeRows;
  }

  const rootEntries = collectProductPriceEntries(p, {});
  if (rootEntries.length > 0) {
    return [
      {
        id: `${p.id}-price-root`,
        showCapital: false,
        capitalValue: 0,
        isAssistance: false,
        premiumValue: rootEntries[0]?.cents ?? 0,
        netPremium: null,
        commissionedPrice: null,
        planName: p.name || "—",
        description: null,
        priceEntries: rootEntries,
      },
    ];
  }

  return [];
}

interface ITableProductsProps {
  products: TProduct[] | undefined;
  deleteProduct: (id: string) => void;
}

export const TableProducts: React.FC<ITableProductsProps> = ({
  products,
  deleteProduct,
}) => {
  const { t } = useI18n();
  const { hasAnyPermission } = usePermissions();
  const [openRowId, setOpenRowId] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const handleRowToggle = (productId: string) => {
    setOpenRowId(openRowId === productId ? null : productId);
  };

  const hasActionPermissions = hasAnyPermission([
    "products:view",
    "products:edit",
    "products:delete",
  ]);

  return (
    <div>
      <Card className="p-4">
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>{t("products.table.name")}</TableHead>
                <TableHead>{t("products.table.supplier")}</TableHead>
                <TableHead className="text-center">{t("products.table.type")}</TableHead>
                <TableHead className="text-center">{t("products.table.vigencyType")}</TableHead>
                <TableHead className="text-center">{t("products.table.lastModified")}</TableHead>
                {hasActionPermissions && (
                  <TableHead>{t("products.table.actions")}</TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {products?.map((p) => (
                <React.Fragment key={p.id}>
                  <TableRow>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleRowToggle(p.id)}
                      >
                        {openRowId === p.id ? (
                          <MinusIcon className="h-4 w-4" />
                        ) : (
                          <PlusIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{p.name}</span>
                          {isInfinitySyncedProduct(p) && (
                            <Badge
                              variant="outline"
                              className="shrink-0 gap-1 text-xs font-normal border-primary/40 text-primary"
                            >
                              <RefreshCw className="h-3 w-3" />
                              {t("products.sync.badge")}
                            </Badge>
                          )}
                        </div>
                        {isInfinitySyncedProduct(p) &&
                          (p.infinity_sync_source_organizations?.length ??
                            0) > 0 && (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-muted-foreground">
                                {t("products.sync.sourceOrganizations")}:
                              </span>
                              <div className="flex flex-wrap gap-1">
                                {p.infinity_sync_source_organizations!.map(
                                  (label) => (
                                    <span
                                      key={label}
                                      className="inline-flex max-w-full rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                                      title={label}
                                    >
                                      {label}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {p.insurance_company_name?.trim()
                        ? p.insurance_company_name
                        : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {p.type === EProductType.INSURANCE_ASSISTANCE
                        ? isInfinityAssistanceProduct(p)
                          ? "Assistências - Infinity"
                          : "Assistência - Soulhub"
                        : t(`product.type.${p.type}`)}
                    </TableCell>
                    <TableCell className="text-center">
                      {t(`product.type_of_vigency.${p.type_of_vigency}`)}
                    </TableCell>
                    <TableCell className="text-center">
                      {new Date(p.updated_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    {hasActionPermissions && (
                      <TableCell>
                        <PermissionGuard
                          permissions={[
                            "products:view",
                            "products:edit",
                            "products:delete",
                          ]}
                          requireAll={false}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <Button size={"sm"} variant={"ghost"}>
                                <EllipsisVerticalIcon className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <PermissionGuard permission="products:view">
                                <DropdownMenuItem
                                  onSelect={() =>
                                    navigate(`/products/${p.id}?mode=view`)
                                  }
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  {t("products.actions.viewDetails")}
                                </DropdownMenuItem>
                              </PermissionGuard>
                              <PermissionGuard permission="products:edit">
                                <DropdownMenuItem
                                  onSelect={() => navigate(`/products/${p.id}`)}
                                >
                                  {t("products.actions.edit")}
                                </DropdownMenuItem>
                              </PermissionGuard>
                              <PermissionGuard permission="products:delete">
                                <DropdownMenuItem
                                  className="bg-red-400"
                                  onSelect={() => {
                                    deleteProduct(p.id);
                                  }}
                                >
                                  <TrashIcon className="h-4 w-4 mr-2" />
                                  {t("generic.delete")}
                                </DropdownMenuItem>
                              </PermissionGuard>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </PermissionGuard>
                      </TableCell>
                    )}
                  </TableRow>
                  {openRowId === p.id &&
                    (() => {
                      const previewRows = [...getProductPreviewRows(p)].sort(
                        (a, b) =>
                          (a.priceEntries[0]?.cents ?? 0) -
                          (b.priceEntries[0]?.cents ?? 0),
                      );
                      return (
                    <TableRow>
                      <TableCell colSpan={hasActionPermissions ? 7 : 6} className="p-0">
                        <div className="p-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {previewRows.length === 0 ? (
                              <p className="text-sm text-muted-foreground col-span-full">
                                {t("products.table.expandEmpty")}
                              </p>
                            ) : (
                              previewRows.map((capital) => (
                                  <Card key={capital.id} className="p-4">
                                    <CardContent className="p-0">
                                      <div className="space-y-2">
                                        {capital.planOnly ? (
                                          <div className="pt-1 border-t border-gray-200 space-y-1">
                                            <p className="text-xs text-gray-600">
                                              <span className="text-gray-500">
                                                {t("products.table.planName")}{" "}
                                              </span>
                                              {capital.planName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              <span className="text-gray-500">
                                                {t("products.table.planDescription")}{" "}
                                              </span>
                                              {capital.description ||
                                                t("products.table.noDescription")}
                                            </p>
                                          </div>
                                        ) : (
                                          <>
                                            {capital.showCapital &&
                                            capital.priceEntries.length === 0 ? (
                                              <div className="flex justify-between items-center gap-2">
                                                <span className="text-sm font-medium text-gray-600">
                                                  {p.type ===
                                                  EProductType.INSURANCE_ASSISTANCE
                                                    ? t(
                                                        "products.table.minimumValue",
                                                      )
                                                    : t("products.table.capital")}
                                                </span>
                                                <span className="text-sm font-semibold text-green-600 shrink-0">
                                                  {formatPrice(
                                                    capital.isAssistance
                                                      ? capital.minimumPrice ?? 0
                                                      : capital.capitalValue,
                                                  )}
                                                </span>
                                              </div>
                                            ) : null}
                                            {capital.priceEntries.map((entry) => (
                                              <div
                                                key={entry.id}
                                                className="flex justify-between items-center gap-2"
                                              >
                                                <span className="text-sm font-medium text-gray-600">
                                                  {t(entry.labelKey)}
                                                </span>
                                                <span className="text-sm font-semibold text-blue-600 shrink-0">
                                                  {formatPrice(entry.cents)}
                                                </span>
                                              </div>
                                            ))}
                                          </>
                                        )}
                                        {!capital.planOnly && (
                                          <div className="pt-2 border-t border-gray-200">
                                            <p className="text-xs text-gray-600">
                                              <span className="text-gray-500">
                                                {t("products.table.previewName")}{" "}
                                              </span>
                                              {capital.planName || t("products.table.noName")}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                              <span className="text-gray-500">
                                                {t("products.table.previewDescription")}{" "}
                                              </span>
                                              {capital.description ||
                                                t("products.table.noDescription")}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                      );
                    })()}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
